import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { CustomerBookingView } from '@/components/scheduling/CustomerBookingView';
import { RescheduleForm } from '@/components/scheduling/RescheduleForm';
import { CancelForm } from '@/components/scheduling/CancelForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ManageBooking() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [view, setView] = useState('booking'); // 'booking' | 'reschedule' | 'cancel' | 'success'
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    if (token) {
      fetchBooking();
    }
  }, [token]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${BACKEND_URL}/api/v1/consultations/${token}/view`);
      
      if (response.status === 404) {
        throw new Error('Booking not found. The link may have expired or is invalid.');
      }
      
      if (response.status === 410) {
        throw new Error('Your access link has expired. Please contact us to get a new link.');
      }
      
      if (!response.ok) {
        throw new Error('Failed to load booking details');
      }
      
      const data = await response.json();
      setBooking(data);
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRescheduleSuccess = (data) => {
    setSuccessData({
      type: 'reschedule',
      newDate: data.new_date,
      newTime: data.new_time,
      formattedDateTime: data.new_datetime_formatted,
      reschedulesRemaining: data.reschedules_remaining
    });
    setView('success');
  };

  const handleCancelSuccess = (data) => {
    setSuccessData({
      type: 'cancel',
      isLate: data.is_late_cancellation
    });
    setView('success');
  };

  // Error State
  if (error && !loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <Alert variant="destructive" data-testid="booking-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="mt-6 text-center">
            <Button data-testid="manage-booking-button-2" onClick={() => navigate('/book-consultation')}>
              Book a New Consultation
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (view === 'success' && successData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <Card data-testid="success-view">
            <CardContent className="pt-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              
              {successData.type === 'reschedule' ? (
                <>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                    Rescheduled Successfully!
                  </h2>
                  <p className="text-gray-600 mb-4">
                    Your consultation has been moved to:
                  </p>
                  <div className="bg-blue-50 rounded-lg p-4 mb-6">
                    <p className="text-lg font-semibold text-blue-900">
                      {successData.formattedDateTime || `${successData.newDate} at ${successData.newTime} ET`}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 mb-6">
                    A confirmation email has been sent. A new Zoom link will be provided before your appointment.
                    {successData.reschedulesRemaining > 0 && (
                      <> You have {successData.reschedulesRemaining} reschedule(s) remaining.</>
                    )}
                  </p>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                    Consultation Cancelled
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Your consultation has been cancelled. A confirmation email has been sent.
                    {successData.isLate && (
                      <span className="block mt-2 text-amber-600">
                        Note: This was a late cancellation (less than 24 hours notice).
                      </span>
                    )}
                  </p>
                </>
              )}
              
              <Button data-testid="manage-booking-button-1" 
                onClick={() => navigate('/book-consultation')}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B99610] text-black"
              >
                {successData.type === 'cancel' ? 'Book New Consultation' : 'Return to Homepage'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4" data-testid="manage-booking-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="mb-6">
          {view !== 'booking' && (
            <Button
              variant="ghost"
              onClick={() => setView('booking')}
              className="mb-4"
              data-testid="back-to-booking"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Booking
            </Button>
          )}
          <h1 className="text-2xl font-semibold text-gray-900">
            {view === 'booking' && 'Your Consultation'}
            {view === 'reschedule' && 'Reschedule Consultation'}
            {view === 'cancel' && 'Cancel Consultation'}
          </h1>
          <p className="text-gray-500">All times are in Eastern Time (ET)</p>
        </div>

        {/* Main Content */}
        {view === 'booking' && (
          <CustomerBookingView
            booking={booking}
            loading={loading}
            onReschedule={() => setView('reschedule')}
            onCancel={() => setView('cancel')}
          />
        )}

        {view === 'reschedule' && booking && (
          <RescheduleForm
            token={token}
            currentDate={booking.scheduled_date}
            currentTime={booking.scheduled_time}
            onSuccess={handleRescheduleSuccess}
            onCancel={() => setView('booking')}
          />
        )}

        {view === 'cancel' && booking && (
          <CancelForm
            token={token}
            scheduledDate={booking.scheduled_date}
            scheduledTime={booking.scheduled_time}
            onSuccess={handleCancelSuccess}
            onCancel={() => setView('booking')}
            onRescheduleInstead={() => setView('reschedule')}
          />
        )}
      </div>
    </div>
  );
}
